/**
 * Sheng Ji's per-game lobby options: throws toggle + Find-the-Friend mode.
 * Rendered as a sub-block of the lobby header card via the game registry.
 */

import type { LobbyOptionsProps } from "../registry.js";

export function ShengJiLobbyOptions({ lobby, isHost, send }: LobbyOptionsProps) {
  const throwsAllowed = lobby.config.throwsAllowed !== false;
  // Find-the-Friend is forced on for odd seat counts (no fixed-pair split).
  const friendModeConfig = lobby.config.friendMode === "find_the_friend";
  const friendModeForced = lobby.seatCount % 2 !== 0;
  const findTheFriend = friendModeForced || friendModeConfig;

  function toggleThrows() {
    send({ t: "set_config", config: { throwsAllowed: !throwsAllowed } });
  }
  function toggleFriendMode() {
    if (friendModeForced) return;
    send({
      t: "set_config",
      config: { friendMode: friendModeConfig ? "fixed" : "find_the_friend" },
    });
  }

  return (
    <div className="lobby-game-options">
      <div className="muted lobby-game-options-title">Sheng Ji options</div>
      <label
        className="row"
        style={{ gap: 8, cursor: isHost ? "pointer" : "default", userSelect: "none" }}
        title="A throw is a lead with multiple combinations at once (e.g. a pair plus a single). Disabled lobbies require every lead to be a single, pair, or tractor."
      >
        <input
          type="checkbox"
          checked={throwsAllowed}
          onChange={isHost ? toggleThrows : undefined}
          disabled={!isHost}
          style={{ width: 16, height: 16 }}
        />
        <span>Allow throws (multi-component leads)</span>
      </label>
      <label
        className="row"
        style={{
          gap: 8,
          cursor: isHost && !friendModeForced ? "pointer" : "default",
          userSelect: "none",
        }}
        title="After burying the kitty, the declarer secretly names one or more cards. Whoever plays a called card becomes a teammate."
      >
        <input
          type="checkbox"
          checked={findTheFriend}
          onChange={isHost && !friendModeForced ? toggleFriendMode : undefined}
          disabled={!isHost || friendModeForced}
          style={{ width: 16, height: 16 }}
        />
        <span>
          Find-the-Friend mode
          {friendModeForced && (
            <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
              (required at {lobby.seatCount} players)
            </span>
          )}
        </span>
      </label>
      {!isHost && (
        <div className="muted" style={{ fontSize: 12 }}>
          Game options are set by the host.
        </div>
      )}
    </div>
  );
}
